import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Sheet } from '../ui/Sheet'
import { Icon } from '../ui/Icon'
import { SlideToConfirm } from '../ui/SlideToConfirm'
import { PixelPet } from '../identity/PixelPet'
import { ForkForm } from './ForkForm'
import { CloneForm } from './CloneForm'
import { agentRestart, getAccountDevices, getNotes, renameAgent, type AgentRow } from '../transport/api'
import { forkLocalOf } from './forkAcross'
import { beginRestart, endRestart } from './restarting'
import { whyRestartFailed } from './restartError'
import { AgentModelSection } from './AgentModelSection'
import { PushToggle } from '../push/PushToggle'
import { BackupForm } from '../backup/BackupForm'
import { isOver, useBackupJob } from '../backup/backupJobs'
import { jobLine } from '../backup/backupWords'
import { ShareForm } from './ShareForm'
import { useAgentShares } from '../friends/agentShares'

/**
 * Everything about ONE agent, behind its face.
 *
 *   owner: 讓使用者可以點選 Agent 的 PixelPet 的 Headshot 來彈出視窗 —
 *          Agent @ server, 所在目錄路徑, [待辦事項 list], 最後是 delete button
 *
 * WHY THE FACE AND NOT A ROW BUTTON. The trash can used to live at the end of every
 * row, one tap from opening the agent, on a 208px rail — a destructive control sitting
 * inside a scrolling list, permanently. Moving it here costs a tap and buys the two
 * things a delete needs: somewhere to say WHAT is about to be closed (the machine, the
 * directory, the work queued against it), and distance from the gesture that merely
 * opens it. The pet is the row's one element that already means "this specific agent"
 * rather than "this row", so it is the natural handle.
 *
 * RENAME AND FORK LIVE HERE TOO — owner, 2026-08-21: 將長按 Agent Header 膠囊彈出來
 * 的選單裡面的功能 (Modify Agent name + Fork this conversation) 合併到 Side Panel
 * 點擊該 Agent 頭像的所彈出的選單裡面. They were a second sheet (SessionMenu) reachable
 * only by a 500ms hold on the header pill — which meant the two sheets about the same
 * agent each held half its actions, and the half behind the hold was the one nothing
 * on screen advertised. Merged, this sheet is the ONE place an agent is administered,
 * and it is reachable from the rail without a timed gesture.
 *
 * THE HOLD IS GONE; TWO GEARS SAY WHERE THIS IS — owner, 2026-10-14: the face in the
 * rail wears a gear where its grey dot was, a gear beside Files in the session's bar
 * opens this too, and the header pill only switches Clean View / Terminal.
 *
 * The name field does NOT autofocus, unlike the menu it came from. There, rename was
 * the whole reason the sheet existed; here the sheet is opened to READ (folder, queue)
 * far more often than to rename, and an autofocused input raises the keyboard over the
 * content on every phone tap.
 */
export function AgentCard({
  agent,
  machine,
  product,
  deviceId,
  onClose,
  onDelete,
  onRenamed,
  onForked,
  deleting,
  endError,
}: {
  agent: AgentRow
  /** The account's name for the machine — the section heading this row sits under. */
  machine: string
  /** "Claude", "Codex"… or null when the machine has not said. */
  product: string | null
  /** The device to browse for the fork's folder — the machine this agent is on. */
  deviceId: string
  onClose: () => void
  onDelete: () => void
  /** The rename landed: the caller re-points anything holding the old name. */
  onRenamed: (newName: string) => void
  /** A fork landed: open it (new session name + the path it was forked into). */
  onForked: (newName: string, host: string | null) => void
  /** True once the kill is in flight, so the button can say so rather than repeat. */
  deleting?: boolean
  /**
   * Why the last End did not happen, in the machine's own words — owner, 2026-09-28:
   * end this agent 這一個按鈕還是沒有效果. A refused or unanswered kill used to leave
   * nothing on the card: the button said "Ending…" and went back, which reads exactly
   * like a button that does nothing.
   */
  endError?: string | null
}) {
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)

  const [name, setName] = useState(agent.session)
  const [nameErr, setNameErr] = useState<string | null>(null)
  const next = name.trim()
  const dirty = next !== '' && next !== agent.session

  // The fork sub-form, opened from its own row so the sheet stays a card until
  // you choose the action.
  // Which small view is open in place of the list (owner, 2026-09-30: one simple list).
  const [view, setView] = useState<'menu' | 'model' | 'queue' | 'backup' | 'fork' | 'clone' | 'share'>('menu')
  /*
   * SHARE — owner, 2026-10-17: 幫我在 Agent 的設定頁面新增一個功能 -> Share. A local agent only:
   * the cloud refuses one on a mounted remote, so the row would only lead to a refusal.
   */
  const canShare = !agent.host && !!deviceId
  const sharesQ = useAgentShares()
  const sharedWith = (sharesQ.data?.outgoing ?? []).filter(
    (s) => s.device_id === deviceId && s.session === agent.session && s.status !== 'expired',
  ).length
  // The backup form, the same way: the card stays a card until the button is pressed.
  const backupJob = useBackupJob(deviceId, agent.session)
  /*
   * Claude, Codex and Hermes (the CLIs whose conversation a restore can put back —
   * Hermes' since 2026-10-15, copied by Hermes' own code on the machine) — and, like
   * Fork, an unknown kind keeps the button: the machine refuses with the reason, which
   * beats hiding something that probably works. Not for an agent on a remote host: the
   * backup runs on the machine that holds the agent's files.
   */
  const canBackup =
    !agent.host && !!deviceId && (product === null || product === 'Claude' || product === 'Codex' || product === 'Hermes')
  const qc = useQueryClient()
  /*
   * The restart itself. `deviceId` is passed and is REQUIRED on the api call for the
   * reason agentKill's comment spells out: /agent/all is asked of each paired machine
   * separately, so this card can be open for an agent on one machine while the relay
   * still points at another, and this account has had the same agent NAME on two.
   *
   * The roster is re-read on success because the pane is new — anything showing its
   * process, its busy state or its uptime is now stale.
   */
  const restart = useMutation({
    /*
     * THE COVER GOES UP BEFORE THE REQUEST, not after it — owner, 2026-09-26: 讓使用者
     * 可以知道, 哦, 現在 Session 還不是能夠繼續看的一個狀態.
     *
     * The pane dies partway through this call, so anything that waited for the response
     * would raise the cover AFTER the terminal had already dissolved — which is the
     * exact moment the person is not supposed to see.
     */
    mutationFn: () => {
      beginRestart(agent.session)
      return agentRestart(agent.session, agent.host ?? null, deviceId)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['agents'] })
    },
    /*
     * BOTH OUTCOMES LOWER IT. A refusal means the agent was never stopped, so the
     * terminal underneath is still live — leaving a cover over a working session would
     * be a lie the person cannot dismiss.
     */
    onSettled: (_d, err) => endRestart(agent.session, err ? 'refused' : 'ok'),
  })

  /*
   * The queue, fetched only while this is open.
   *
   * The rail already shows a COUNT — this is the one place the contents are worth the
   * request, and asking for every agent's notes to render a sidebar would be one
   * relayed round trip per row.
   */
  /*
   * Same cached query the fork form's machine picker already uses (`['menu-machines']`),
   * so this costs no request — react-query hands back the rows that are already there.
   */
  const devQ = useQuery({ queryKey: ['menu-machines'], queryFn: getAccountDevices, staleTime: 30_000 })
  const canFork = forkLocalOf(devQ.data?.devices, agent.hash) !== false

  const notes = useQuery({
    // device_id in the key as well as the call: two machines can run sessions with
    // the same name, and a shared cache entry would show one agent's queue on the
    // other's card.
    queryKey: ['agent-notes', agent.device_id ?? '', agent.host ?? '', agent.session],
    queryFn: () => getNotes(agent.session, agent.host, agent.device_id),
  })
  const items = notes.data?.notes ?? []

  const save = async () => {
    if (!dirty) return
    setBusy(true)
    setNameErr(null)
    try {
      const r = await renameAgent(agent.session, next, agent.host)
      if (r.error) throw new Error(r.message || r.error)
      onRenamed(r.session ?? next)
      onClose()
    } catch (e) {
      setNameErr(String(e instanceof Error ? e.message : e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Sheet
      title={agent.session}
      onClose={onClose}
      /*
       * ONE NAME, ONCE — owner, 2026-09-09: 點擊 Agent 頭像彈出的視窗的空間利用很不理想.
       * Agent name 那個欄位基本上可以跟 Title bar 上面那個 agent name 合併為同一個東西.
       * Claude @ aowl 這個字串應該可以跟 Agent name 放在同一排. 目前設計的 agent name
       * 出現了三次 (這是要幹麻?).
       *
       * The title bar IS the name field: the pet, the name (editable in place), the
       * product and machine on the same row, and Rename lit only once the name has
       * changed. The separate identity block, the AGENT NAME section and its hint
       * about addresses are gone — the sheet's title said the name, the block said
       * it again, and the field said it a third time.
       */
      head={
        <div className="ac-hd">
          <PixelPet name={agent.session} seed={agent.hash} size={28} active={false} />
          <input
            className="ac-title"
            aria-label="Agent name"
            value={name}
            spellCheck={false}
            autoCapitalize="off"
            autoCorrect="off"
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void save()
              if (e.key === 'Escape') onClose()
            }}
          />
          {/* "Claude @ aowl" — wanted here even though the rail's rows dropped it: this
              sheet can be reached from a search result or a link where the machine
              heading is not on screen at all. */}
          {/* A long agent name wins the row; the machine label yields with an ellipsis
              and keeps its full text on the tooltip. */}
          <span className="ac-where" title={product ? `${product} @ ${machine}` : machine}>
            {product ? `${product} @ ${machine}` : machine}
          </span>
          {/* Rename appears once there is something to rename: on the resting card the
              row's width belongs to the name (a long one was cut to "wan22-mega8-hyp"
              beside a permanently-disabled button). */}
          {(dirty || busy) && (
            <button
              type="button"
              className="sheet-btn primary ac-rename"
              disabled={busy}
              onClick={() => void save()}
            >
              {busy ? '…' : 'Rename'}
            </button>
          )}
        </div>
      }
    >
      {nameErr && <p className="err-line sm-err">{nameErr}</p>}

      {/*
        ONE LIST OF ROWS — owner, 2026-09-30: 這個 Agent 彈出來的設定選單 … 太複雜太亂了 …
        簡單簡潔的設計風格會更好, 你裡面放了一堆不必要的文字. Each action is one row, an icon
        and a short label; a row that needs more opens its own small view in place, with a
        back row. The explanations that used to sit under every section are gone — the
        screen says what each thing is, not why it exists.
      */}
      {view === 'menu' ? (
        <>
          <div className="ac-path" title={agent.cwd || undefined}>
            {agent.cwd || <span className="ac-none">Folder not reported</span>}
          </div>
          <div className="ac-menu">
            <PushToggle deviceId={deviceId} channelHash={agent.hash} />
            <button type="button" className="ac-row" onClick={() => setView('model')}>
              <Icon name="tune" size={18} />
              <span className="ac-row-label">Model</span>
              <Icon name="chevron_right" size={18} className="ac-row-go" />
            </button>
            {items.length > 0 && (
              <button type="button" className="ac-row" onClick={() => setView('queue')}>
                <Icon name="inbox" size={18} />
                <span className="ac-row-label">Queued messages</span>
                <span className="ac-row-val">{items.length}</span>
                <Icon name="chevron_right" size={18} className="ac-row-go" />
              </button>
            )}
            <button
              type="button"
             
              className="ac-row"
              disabled={restart.isPending}
              onClick={() => restart.mutate()}
            >
              <Icon name="refresh" size={18} />
              <span className="ac-row-label">{restart.isPending ? 'Restarting…' : 'Restart'}</span>
              {restart.isSuccess && <span className="ac-row-val ac-ok">Restarted</span>}
            </button>
            {restart.isError && <p className="ac-row-err">{whyRestartFailed(restart.error)}</p>}
            {canBackup && (
              <button type="button" className="ac-row" onClick={() => setView('backup')}>
                <Icon name="cloud_upload" size={18} />
                <span className="ac-row-label">Back up</span>
                {backupJob && !isOver(backupJob) && <span className="ac-row-val">{jobLine(backupJob, machine)}</span>}
                <Icon name="chevron_right" size={18} className="ac-row-go" />
              </button>
            )}
            {canShare && (
              <button type="button" className="ac-row" onClick={() => setView('share')}>
                <Icon name="share" size={18} />
                <span className="ac-row-label">Share</span>
                {sharedWith > 0 && (
                  <span className="ac-row-val">
                    {sharedWith === 1 ? '1 friend' : `${sharedWith} friends`}
                  </span>
                )}
                <Icon name="chevron_right" size={18} className="ac-row-go" />
              </button>
            )}
            {canFork && (
              <button type="button" className="ac-row" onClick={() => setView('fork')}>
                <Icon name="call_split" size={18} />
                <span className="ac-row-label">Fork</span>
                <Icon name="chevron_right" size={18} className="ac-row-go" />
              </button>
            )}
            {!agent.host && !!deviceId && (
              <button type="button" className="ac-row" onClick={() => setView('clone')}>
                <Icon name="content_copy" size={18} />
                {/* Owner, 2026-10-02: marked Beta until he has tested it fully. */}
                <span className="ac-row-label">
                  Clone <span className="ac-beta">Beta</span>
                </span>
                <Icon name="chevron_right" size={18} className="ac-row-go" />
              </button>
            )}
            {/* End turns INTO its slide in place (owner, 2026-09-28): a stray tap cannot end an agent. */}
            {confirming ? (
              <div className="ac-row ac-row-slide">
                <SlideToConfirm
                  label={`Slide to end ${agent.session}`}
                  busyLabel="Ending…"
                  busy={deleting ?? false}
                  onConfirm={onDelete}
                />
                <button
                  type="button"
                  className="ac-act ac-end-cancel"
                  aria-label="Keep it"
                  title="Keep it"
                  onClick={() => setConfirming(false)}
                  disabled={deleting ?? false}
                >
                  <Icon name="close" size={16} />
                </button>
              </div>
            ) : (
              <button
                type="button"
               
                className="ac-row ac-row-danger"
                onClick={() => setConfirming(true)}
                disabled={deleting}
              >
                <Icon name="delete" size={18} />
                <span className="ac-row-label">End agent</span>
              </button>
            )}
            {confirming && endError && !deleting && (
              <p className="ac-row-err" role="alert" data-testid="end-error">
                Could not end it: {endError}
              </p>
            )}
          </div>
        </>
      ) : (
        <>
          <button type="button" className="ac-back" aria-label="Back" onClick={() => setView('menu')}>
            <Icon name="chevron_left" size={18} />
            {view === 'model' ? 'Model' : view === 'queue' ? 'Queued messages' : view === 'backup' ? 'Back up' : view === 'clone' ? 'Clone' : view === 'share' ? 'Share' : 'Fork'}
          </button>
          {view === 'model' && <AgentModelSection agent={agent} deviceId={deviceId} />}
          {view === 'share' && canShare && <ShareForm agent={agent} deviceId={deviceId} machine={machine} />}
          {view === 'queue' && (
            notes.isError ? (
              <p className="err-line gh-error">{String((notes.error as Error)?.message ?? notes.error)}</p>
            ) : (
              <ul className="ac-todo">
                {items.map((n) => (
                  <li key={n.id}>
                    <span className="ac-todo-text">{n.text}</span>
                    {n.attachments && n.attachments.length > 0 && (
                      <span className="ac-todo-files" title={`${n.attachments.length} file(s)`}>
                        <Icon name="attach_file" size={11} />
                        {n.attachments.length}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )
          )}
          {view === 'backup' && canBackup && (
            <BackupForm agent={agent} deviceId={deviceId} machine={machine} onBack={() => setView('menu')} />
          )}
          {view === 'clone' && deviceId && (
            <CloneForm
              agent={agent}
              product={product}
              deviceId={deviceId}
              machine={machine}
              onCloned={(newName) => {
                onForked(newName, null)
                onClose()
              }}
            />
          )}
          {view === 'fork' && canFork && (
            <ForkForm
              agent={agent}
              sourceDeviceId={deviceId}
              machine={machine}
              onBack={() => setView('menu')}
              onForked={(newName, host) => {
                onForked(newName, host)
                onClose()
              }}
            />
          )}
        </>
      )}
    </Sheet>
  )
}
